import {
    Directive,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import flatpickr from 'flatpickr';
import { Instance } from 'flatpickr/dist/types/instance';
import { BaseOptions } from 'flatpickr/dist/types/options';

@Directive({
    selector: '[appFlatpickr]',
    exportAs: 'appFlatpickr',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => FlatPickrDirective),
        },
    ],
    standalone: false,
})
export class FlatPickrDirective
    implements OnInit, OnChanges, ControlValueAccessor, OnDestroy
{
    @Input() public config: Partial<BaseOptions> = {};
    @Output() public flatPickrChange = new EventEmitter<
        Date | Date[] | undefined
    >();
    private onChange: (date: Date | Date[] | undefined) => void;
    private onTouched: () => void;
    private instance: Instance;

    public constructor(private elementRef: ElementRef) {}

    public ngOnInit(): void {
        // noinspection JSUnusedGlobalSymbols
        const config: Partial<BaseOptions> = {
            onChange: selectedDates => {
                let value: Date | Date[] | undefined;

                if (config.mode === 'single') {
                    value = selectedDates[0];
                } else if (config.mode === 'range') {
                    if (selectedDates.length === 1) {
                        this.instance.set('minDate', selectedDates[0]);
                    } else if (selectedDates.length === 2) {
                        value = selectedDates;
                        this.instance.set(
                            'minDate',
                            this.config.minDate || undefined
                        );
                    }
                }

                if (value !== undefined) {
                    this.reportChanged(value);
                } else if (selectedDates.length === 0) {
                    // Selection was fully cleared — propagate undefined so
                    // form controls and ngModel reflect the empty state.
                    this.reportChanged(undefined);
                    this.jumpToEnableDate();
                } else {
                    // Partial range (one date chosen) — keep the state
                    // internal until the second date is selected.
                    this.jumpToEnableDate();
                }
            },
            onClose: () => {
                if (config.mode === 'range') {
                    // Keep the first date as the temporary minimum while the
                    // range is incomplete so reopening still blocks an earlier
                    // second-date pick. Only restore the configured minimum
                    // once the range is complete or the selection is cleared.
                    const isPartial = this.instance.selectedDates.length === 1;
                    this.instance.set(
                        'minDate',
                        isPartial
                            ? this.instance.selectedDates[0]
                            : this.config.minDate || undefined
                    );
                }
            },
            onOpen: () => this.jumpToEnableDate(),
            ...{
                mode: 'single',
            },
            ...this.config,
        };

        this.instance = flatpickr(this.elementRef.nativeElement, config);
        this.jumpToEnableDate();
    }

    public ngOnChanges(): void {
        this.instance?.set(this.config);

        this.jumpToEnableDate();
    }

    public ngOnDestroy(): void {
        this.instance?.destroy();
    }

    public clear(): void {
        // instance.clear() fires Flatpickr's onChange([]) synchronously,
        // which calls reportChanged(undefined) via our onChange handler above.
        this.instance.clear();

        if (this.config.mode === 'range') {
            this.instance.set('minDate', this.config.minDate || undefined);
        }
    }

    public writeValue(obj: any): void {
        if (!this.instance) {
            return;
        }
        this.instance.setDate(obj);
    }

    public setDisabledState(isDisabled: boolean): void {
        this.instance.input.disabled = isDisabled;
    }

    public registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    private reportChanged(value: Date | Date[] | undefined): void {
        if (this.onTouched) {
            this.onTouched();
        }
        if (this.onChange) {
            this.onChange(value);
        }
        this.flatPickrChange.emit(value);
    }

    private jumpToEnableDate(): void {
        if (this.instance?.selectedDates?.length) return;

        const defaultFromDate = this.config.enable?.[0]?.['from'];

        if (!defaultFromDate) return;

        this.instance?.jumpToDate(defaultFromDate);
    }
}
